import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar } from './Avatar';
import type { CrewId } from '../data/crew';

/**
 * One post on the feed, Instagram-shaped (owner, 2026-09-29: "visuals first"):
 * a slim header, then the picture edge to edge at 4:5, and any words laid over
 * its foot on a scrim — two lines, tap to unfold the rest. More than one
 * picture swipes, with dots. Double-tap hearts it. Everything that is not the
 * picture or the heart (make private, delete, report) is behind the ···.
 */

export type Slide =
  | { kind: 'image'; url: string; fit?: 'cover' | 'sheet'; pill?: string; caption?: ReactNode; more?: ReactNode; onOpen?: () => void }
  | { kind: 'video'; url: string; poster?: string; pill?: string; caption?: ReactNode; more?: ReactNode }
  | { kind: 'words'; text: string; colour: string; portrait?: string; pill?: string; caption?: ReactNode; more?: ReactNode }
  | { kind: 'wait'; text: string; cta?: string; pill?: string; onOpen?: () => void; dark?: boolean };

export interface MenuItem { label: string; onClick: () => void; danger?: boolean }

export function Post({ who, name, sub, slides, hearted, onHeart, audioUrl, menu, className = '' }: {
  who: CrewId | 'me'; name: string; sub: ReactNode; slides: Slide[];
  hearted?: boolean; onHeart?: () => void; audioUrl?: string; menu: MenuItem[]; className?: string;
}) {
  const [at, setAt] = useState(0);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pop, setPop] = useState(0);
  const track = useRef<HTMLDivElement>(null);
  const tap = useRef<{ t: number; timer?: number }>({ t: 0 });

  const onScroll = () => { const el = track.current; if (el) setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); };
  // one tap opens the picture, two in quick succession heart it
  const onTap = (s: Slide) => {
    const now = Date.now();
    if (onHeart && now - tap.current.t < 280) {
      window.clearTimeout(tap.current.timer);
      tap.current.t = 0;
      setPop((n) => n + 1);
      if (!hearted) onHeart();
      return;
    }
    tap.current.t = now;
    const go = 'onOpen' in s ? s.onOpen : undefined;
    if (go) tap.current.timer = window.setTimeout(go, onHeart ? 280 : 0);
  };

  return (
    <article className={`post ${className}`}>
      <header className="post-head">
        <Avatar who={who} size={34} name={name} />
        <div className="post-who"><b>{name}</b><span>{sub}</span></div>
        {menu.length > 0 && <button type="button" className="post-more" aria-label="Post options" onClick={() => setMenuOpen(true)}>···</button>}
      </header>

      <div className="slides-wrap">
        <div className="slides" ref={track} onScroll={onScroll}>
          {slides.map((s, i) => (
            <div className={`slide ${s.kind} ${s.kind === 'image' && s.fit === 'sheet' ? 'sheet-fit' : ''} ${s.kind === 'wait' && s.dark ? 'dark' : ''}`} key={i} onClick={() => onTap(s)}
              style={s.kind === 'words' ? { ['--ink-bg' as string]: s.colour } : undefined}>
              <SlideBody s={s} active={i === at} />
              {s.pill && <span className="slide-pill">{s.pill}</span>}
              {'caption' in s && s.caption && (
                <button type="button" className={`slide-cap ${open ? 'open' : ''}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-expanded={open}>
                  <span className="cap-line">{s.caption}</span>
                  {open ? s.more && <span className="cap-more">{s.more}</span> : s.more && <span className="cap-hint">more</span>}
                </button>
              )}
            </div>
          ))}
        </div>
        {slides.length > 1 && <span className="slide-count">{at + 1}/{slides.length}</span>}
        {pop > 0 && <span className="heart-pop" key={pop} aria-hidden>♥</span>}
      </div>

      {(onHeart || audioUrl || slides.length > 1) && (
        <footer className="post-actions">
          {onHeart ? <button type="button" className={`heart ${hearted ? 'on' : ''}`} onClick={onHeart} aria-label="Heart" aria-pressed={!!hearted}>{hearted ? '♥' : '♡'}</button> : <span />}
          {slides.length > 1 ? <span className="dots" aria-hidden>{slides.map((_, i) => <i key={i} className={i === at ? 'on' : ''} />)}</span> : <span />}
          {audioUrl ? <Listen url={audioUrl} /> : <span />}
        </footer>
      )}

      {menuOpen && (
        <div className="action-sheet" role="dialog" aria-label="Post options" onClick={() => setMenuOpen(false)}>
          <div className="action-list" onClick={(e) => e.stopPropagation()}>
            {menu.map((m) => <button type="button" key={m.label} className={m.danger ? 'danger' : ''} onClick={() => { setMenuOpen(false); m.onClick(); }}>{m.label}</button>)}
            <button type="button" className="cancel" onClick={() => setMenuOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </article>
  );
}

function SlideBody({ s, active }: { s: Slide; active: boolean }) {
  if (s.kind === 'image') return s.fit === 'sheet' ? <div className="sheet-crop"><img src={s.url} alt="" /></div> : <img className="slide-img" src={s.url} alt="" loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />;
  if (s.kind === 'video') return <AutoVideo url={s.url} poster={s.poster} active={active} />;
  if (s.kind === 'words') return (<>
    {s.portrait && <img className="words-portrait" src={s.portrait} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />}
    <p className={`words-text ${s.text.length > 140 ? 'long' : s.text.length > 70 ? 'mid' : ''}`}>{s.text}</p>
  </>);
  return <div className="wait-body"><span className="mini-back" /><span>{s.text}</span>{s.cta && <span className="wait-cta">{s.cta}</span>}</div>;
}

/** A film plays like a reel: silent and looping while it is on screen, tap for sound. */
function AutoVideo({ url, poster, active }: { url: string; poster?: string; active: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && active) v.play().catch(() => {}); else v.pause(); }, { threshold: 0.6 });
    io.observe(v);
    return () => io.disconnect();
  }, [active]);
  return (<>
    <video ref={ref} className="slide-img" src={url} poster={poster} muted={muted} loop playsInline preload="metadata" onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }} />
    <span className="sound-pill" aria-hidden>{muted ? '🔇' : '🔊'}</span>
  </>);
}

/** The recording under a told piece, as a small pill rather than a whole player bar. */
function Listen({ url }: { url: string }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [on, setOn] = useState(false);
  useEffect(() => () => { ref.current?.pause(); }, []);
  const toggle = () => {
    if (!ref.current) { ref.current = new Audio(url); ref.current.onended = () => setOn(false); }
    if (on) { ref.current.pause(); setOn(false); } else { ref.current.play().then(() => setOn(true)).catch(() => setOn(false)); }
  };
  return <button type="button" className={`listen ${on ? 'on' : ''}`} onClick={toggle}>{on ? '❚❚ Listening' : '▶ Listen'}</button>;
}
